import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Instagram, Ghost, Send } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";

const PHONE = "0776069799";
const PHONE_DISPLAY = "07 76 06 97 99";
const WHATSAPP = "33776069799";
const EMAIL = "novacleanservice@outlook.fr";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return toast.error("Merci de compléter les champs obligatoires");
    const text = [
      "Bonjour,",
      "",
      form.message,
      "",
      `Nom : ${form.name}`,
      `Téléphone : ${form.phone}`,
      form.email ? `Email : ${form.email}` : null,
      "",
      "Merci.",
    ].filter((l) => l !== null).join("\n");
    openWhatsApp(text);
    toast.success("Redirection vers WhatsApp… Votre message est prêt à être envoyé.");
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Info */}
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">
              / 07 — Contactez-nous
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
              Une question ?<br />
              <span className="text-gradient-red">Parlons-en.</span>
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-10 max-w-lg">
              Devis gratuit, conseil personnalisé ou information sur nos prestations : contactez-nous par le canal qui vous arrange.
            </p>

            <div className="space-y-4 mb-10">
              <ContactRow
                icon={Phone}
                label="Téléphone"
                value={PHONE_DISPLAY}
                href={`tel:${PHONE}`}
                testid="contact-phone"
              />
              <ContactRow
                icon={MessageCircle}
                label="WhatsApp"
                value="Discuter en direct"
                href={`https://wa.me/${WHATSAPP}`}
                testid="contact-whatsapp"
                accent
              />
              <ContactRow
                icon={Mail}
                label="Email"
                value={EMAIL}
                href={`mailto:${EMAIL}`}
                testid="contact-email"
              />
              <ContactRow
                icon={MapPin}
                label="Zone d'intervention"
                value="Versailles + 20 km autour · Déplacement offert"
                testid="contact-zone"
              />
            </div>

            <div className="pt-6 border-t border-white/10">
              <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4 font-mono">// Réseaux sociaux</div>
              <div className="flex flex-wrap gap-3">
                <Social href="https://instagram.com/nova.cleanservice" label="Instagram" handle="nova.cleanservice" icon={Instagram} testid="social-instagram" />
                <Social href="https://www.tiktok.com/@cleanservicenova" label="TikTok" handle="cleanservicenova" icon={TikTokIcon} testid="social-tiktok" />
                <Social href="https://www.snapchat.com/add/novacleanservi" label="Snapchat" handle="novacleanservi" icon={Ghost} testid="social-snapchat" />
                <Social href="https://share.google/0LlOgEufci4wgLguz" label="Google" handle="Voir nos avis" prefix="" icon={GoogleIcon} testid="social-google" />
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="p-8 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl space-y-5" data-testid="contact-form">
            <h3 className="font-bold text-xl text-white mb-2">Envoyez-nous un message</h3>

            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Nom complet *</label>
              <input
                type="text"
                value={form.name}
                onChange={change("name")}
                required
                data-testid="contact-name-input"
                className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white focus:outline-none focus:border-[#E50914] transition-colors"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Téléphone *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={change("phone")}
                  required
                  data-testid="contact-phone-input"
                  className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white focus:outline-none focus:border-[#E50914]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={change("email")}
                  data-testid="contact-email-input"
                  className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white focus:outline-none focus:border-[#E50914]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Message *</label>
              <textarea
                value={form.message}
                onChange={change("message")}
                rows="5"
                required
                data-testid="contact-message-input"
                className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white resize-none focus:outline-none focus:border-[#E50914]"
                placeholder="Votre demande, véhicule, besoin particulier…"
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold uppercase text-sm tracking-wider px-6 py-4 rounded border border-[#25D366] hover:shadow-[0_0_32px_rgba(37,211,102,0.4)] transition-all"
            >
              <Send className="w-4 h-4" /> Envoyer via WhatsApp
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid, accent }) {
  const inner = (
    <div className={`flex items-center gap-4 p-4 bg-[#0A0A0A] border rounded-lg transition-all ${accent ? 'border-[#25D366]/40 hover:border-[#25D366] hover:shadow-[0_0_20px_rgba(37,211,102,0.25)]' : 'border-white/10 hover:border-[#E50914]/50'}`}>
      <div className={`w-11 h-11 rounded flex items-center justify-center shrink-0 ${accent ? 'bg-[#25D366]/15 border border-[#25D366]/40' : 'bg-[#E50914]/10 border border-[#E50914]/30'}`}>
        <Icon className={`w-5 h-5 ${accent ? 'text-[#25D366]' : 'text-[#E50914]'}`} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">{label}</div>
        <div className="text-white font-semibold truncate">{value}</div>
      </div>
    </div>
  );
  if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid}>{inner}</a>;
  return <div data-testid={testid}>{inner}</div>;
}

function Social({ href, label, handle, icon: Icon, testid, prefix = "@" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="group flex items-center gap-3 px-4 py-2.5 bg-white/5 border border-white/10 rounded-full hover:border-[#E50914]/50 hover:bg-white/10 transition-all"
    >
      <Icon className="w-4 h-4 text-white group-hover:text-[#E50914] transition-colors" />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
        <div className="text-xs font-semibold text-white">{prefix}{handle}</div>
      </div>
    </a>
  );
}

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.3l6.3 5.3C41.2 35.9 44 30.4 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.79a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.22z"/>
    </svg>
  );
}
